import { useCallback, useEffect, useMemo, useRef, useState, type ComponentProps, type ReactNode } from 'react'
import { scrollToCreatedCard, cssEscapeId } from '../../ui/reveal-card.ts'
import { bridgeCall, errorMessage } from '../../data/bridge-client.ts'
import { isManagedConfigField } from '../../../shared/managed-config-fields.ts'
import { instructionFileIdOf } from '../../data/prompt-config-content.ts'
import type { PromptToolTranslate } from '../../locales.ts'
import { MenuSelect } from '../../ui/MenuSelect.tsx'
import { PromptConfigCard } from './PromptConfigCard.tsx'
import { moveToView, moveWithinLayer, promptConfigLayer, promptConfigViewOrder, sameConfigPosition, viewOrderedIds } from '../../data/prompt-config-order.ts'
import { useModuleConfigOrder } from '../../data/use-module-config-order.ts'
import { configIdentityKey } from '../../../shared/module-config-order.ts'
import { displayLayers, LAYER_LABEL_KEYS, matchesConfigKeyword, translateLabel } from './prompt-config-policy.ts'
import type { EngineMeta, PromptConfigDraft, ValidationErrorEntry } from '../../prompt-tool-types.ts'
import type { InstructionPolicyFileOverride, InstructionPolicySnapshot } from '../../../shared/instructions.ts'
import sharedCss from '../../ui/controls.module.css'
import featureCss from './prompts.module.css'

const styles = { ...sharedCss, ...featureCss }

export interface PromptConfigListProps {
  t: PromptToolTranslate
  meta: EngineMeta
  configs: PromptConfigDraft[]
  browse?: { filter: string; expanded?: string }
  fieldDrafts?: ComponentProps<typeof PromptConfigCard>['fieldDrafts']
  draftScope?: string
  readOnlyReason?: string
  onCreate?: () => void
  onChoosePreset?: () => void
  createdHidden?: boolean
  onShowCreated?: () => void
  /** 传入 layer 时只展示该层配置；不传展示全部配置。 */
  layer?: string
  /** 传入 scope 时按 audience 作用域过滤：main = 非仅子代理（主会话可见）；subagent = 非仅主会话（子代理可见；缺省公用两边都可见）。 */
  scope?: 'main' | 'subagent'
  /** 列表工具栏追加操作（如「新建模板」）。 */
  extraActions?: ReactNode
  /** 工具栏之后、配置卡片之前的页面级诊断卡与定位 effect。 */
  beforeCards?: ReactNode
  /** 工具栏中的非提示词配置操作（如能力创建）。 */
  toolbarActions?: ReactNode
  /** 受控层筛选（全部/世界书/层级）；未传时内部 state 兜底（子代理页等独立实例）。 */
  viewFilter?: string
  onViewFilterChange?: (value: string) => void
  /** 受控搜索词：页面持有搜索词时（例如同时过滤能力卡与共享设置区）由页面下发并接收改动。 */
  keyword?: string
  onKeywordChange?: (value: string) => void
  /** 只响应明确的创建动作；后台读取已有配置不抢占筛选或展开状态。 */
  createdConfigId?: string
  /** 空状态追加提示（如「当前模块模板该层无配置」）。 */
  emptyHint?: string
  onPatchConfigs: (configs: PromptConfigDraft[]) => void
  onSaveConfigs: (configs: PromptConfigDraft[]) => Promise<boolean>
  onPrepareOrder?: () => Promise<boolean>
  onReloadConfigs?: () => Promise<boolean>
  onSaveInstructions?: () => Promise<boolean>
  instructionPolicy?: InstructionPolicySnapshot
  /** 指令文件卡：显式写盘与重新读取（不经模块保存路径）。 */
  onSaveInstructionFile?: (fileId: string) => void
  onReloadInstructionFile?: (fileId: string) => void
  /** 指令文件卡的行为策略改动（独立策略存储）。 */
  onPatchInstructionPolicy?: (fileId: string, override: InstructionPolicyFileOverride) => void
  /**
   * 本层引擎设置内容（app 层注入，逐层下发到每张实例卡）：
   * 同层每张卡显示同一份值，折叠区默认折叠且展开才渲染内容。
   */
  renderLayerSettings?: (layer: string, config: PromptConfigDraft) => ReactNode
  /** 该层有可编辑设置时，在现有实例卡内提供设置区。 */
  hasLayerSettings?: (layer: string) => boolean
  matchesLayerSettings?: (layer: string, keyword: string) => boolean
  onNotice: (kind: 'ok' | 'error', message: string) => void
}

/** 共享的提示词配置列表：校验、保存、脏检测、复制、删除、层内移动。 */
export function PromptConfigList(props: PromptConfigListProps): ReactNode {
  const { t, meta, layer, scope, extraActions, beforeCards, toolbarActions, viewFilter: viewFilterProp, onViewFilterChange, emptyHint, onSaveConfigs, onSaveInstructionFile, onReloadInstructionFile, onPatchInstructionPolicy, onNotice } = props
  const [acrossModules, setAcrossModules] = useState(false)
  const moduleOrder = useModuleConfigOrder(t, props.onReloadConfigs)
  const configs = acrossModules ? moduleOrder.configs : props.configs
  const onPatchConfigs = useCallback((next: PromptConfigDraft[]): void => {
    if (acrossModules) void moduleOrder.save(next)
    else props.onPatchConfigs(next)
  }, [acrossModules, moduleOrder.save, props.onPatchConfigs])
  const [expanded, setExpanded] = useState<string | undefined>(props.browse?.expanded)
  const [errors, setErrors] = useState<ValidationErrorEntry[]>([])
  const [validating, setValidating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [filterState, setFilterState] = useState(props.browse?.filter ?? '')
  const changeFilter = (value: string): void => {
    if (props.browse !== undefined) props.browse.filter = value
    props.onKeywordChange?.(value)
    setFilterState(value)
  }
  const [createdId, setCreatedId] = useState<string>()
  const busyRef = useRef(false)
  const rangeEpoch = useRef(0)
  useEffect(() => {
    setAcrossModules(false)
    setSaving(false)
    busyRef.current = false
    moduleOrder.reset()
    return () => { rangeEpoch.current += 1 }
  }, [props.draftScope, moduleOrder.reset])
  const changeRange = async (value: string): Promise<void> => {
    const next = value === 'all-modules'
    if (next === acrossModules || busyRef.current || moduleOrder.busy) return
    const request = rangeEpoch.current
    busyRef.current = true
    setSaving(true)
    try {
      if (next) {
        if (!await props.onPrepareOrder?.() || request !== rangeEpoch.current) return
        setAcrossModules(true)
        await moduleOrder.load()
      } else {
        if (!await props.onReloadConfigs?.() || request !== rangeEpoch.current) return
        moduleOrder.reset()
        setAcrossModules(false)
      }
    } catch (cause) {
      if (request === rangeEpoch.current) onNotice('error', errorMessage(cause))
    } finally {
      if (request === rangeEpoch.current) { busyRef.current = false; setSaving(false) }
    }
  }
  // 状态反馈统一走全局通知（渲染在工作台标题行），本页不再自留一份 feedback。
  const report = (kind: 'ok' | 'error', message: string): void => {
    onNotice(kind, message)
  }
  useEffect(() => {
    if (!acrossModules && props.browse !== undefined) props.browse.expanded = expanded
  }, [expanded, props.browse, acrossModules])
  /** 拖拽排序状态：源卡片 id + 落点（目标 id + 前/后）。 */
  const [dragId, setDragId] = useState<string | undefined>(undefined)
  const [dropTarget, setDropTarget] = useState<{ id: string; before: boolean } | undefined>(undefined)
  /** 过滤下拉：全部 / 世界书（策略）/ 各注入层级。外部 layer prop 传入时固定该层。 */
  const [innerViewFilter, setInnerViewFilter] = useState<string>('all')
  const viewFilter = viewFilterProp ?? innerViewFilter
  const changeViewFilter = (value: string): void => {
    if (onViewFilterChange !== undefined) onViewFilterChange(value)
    else setInnerViewFilter(value)
  }
  useEffect(() => {
    if (acrossModules) return
    const created = configs.find((config) => config.id === props.createdConfigId)
    if (created === undefined) return
    setCreatedId(created.id)
    // 只展开并定位新建的卡：不改动用户的层级筛选与搜索词。
    setExpanded(created.id)
    return scrollToCreatedCard(`[data-config-id="${cssEscapeId(created.id)}"]`)
  }, [props.createdConfigId])

  const effectiveLayer = layer ?? (viewFilter !== 'all' && viewFilter !== 'world-book' ? viewFilter : undefined)
  const allLayers = displayLayers(meta.layerOrder, [...meta.layers, ...configs.map(promptConfigLayer)])
  const visible = effectiveLayer === undefined
    ? configs
    : configs.filter((config) => promptConfigLayer(config) === effectiveLayer)
  const scoped = scope === undefined
    ? visible
    : visible.filter((config) => {
      const mode = config.audience
      return scope === 'main' ? mode !== 'subagent' : mode !== 'main'
    })
  const byStrategy = viewFilter !== 'world-book'
    ? scoped
    : scoped.filter((config) => config.strategy === 'world-book')
  // 搜索词：受控时取页面下发值（同一搜索词同时过滤能力卡与共享设置区），否则用内部 state。
  const filter = props.keyword ?? filterState
  const keyword = filter.trim().toLowerCase()
  const configMatches = byStrategy.filter((config) => matchesConfigKeyword(config, keyword, t))
  const batchConfigs = configMatches.filter((config) => !isManagedConfigField(config, 'enabled'))
  const filtered = keyword.length === 0 ? byStrategy : byStrategy.filter((config) =>
    configMatches.includes(config) || (viewFilter !== 'world-book' && props.matchesLayerSettings?.(promptConfigLayer(config), keyword) === true))
  // 插入点分组只用于阅读；order 仅在同一插入点内比较。
  const layerRank = (config: PromptConfigDraft): number => {
    const index = allLayers.indexOf(promptConfigLayer(config))
    return index < 0 ? allLayers.length : index
  }
  const ordered = filtered
    .map((config, index) => ({ config, index }))
    .sort((a, b) => {
      const byLayer = layerRank(a.config) - layerRank(b.config)
      if (byLayer !== 0) return byLayer
      const byOrder = promptConfigViewOrder(a.config) - promptConfigViewOrder(b.config)
      if (byOrder !== 0) return byOrder
      const bySequence = (a.config.sequence ?? 0) - (b.config.sequence ?? 0)
      if (bySequence !== 0) return bySequence
      return a.index - b.index
    })
    .map((entry) => entry.config)
  useEffect(() => {
    if (!acrossModules && expanded !== undefined && !configs.some((config) => config.id === expanded)) setExpanded(undefined)
  }, [configs, expanded, acrossModules])
  /** 按过滤后可见配置一次性启用/禁用（批量开关）。 */
  const batchSetEnabled = (enabled: boolean): void => {
    if (props.readOnlyReason !== undefined) return
    const visibleIds = new Set(batchConfigs.map((config) => config.id))
    onPatchConfigs(configs.map((config) => visibleIds.has(config.id) ? { ...config, enabled } : config))
  }

  const runValidate = async (target: PromptConfigDraft[], announceSuccess = true): Promise<boolean> => {
    setValidating(true)
    try {
      const res = await bridgeCall('configsValidate', { promptConfigs: target })
      if (!res.ok) {
        report('error', t('configs.notice.validateFailed', { reason: res.message ?? 'settings bridge unavailable' }))
        return false
      }
      setErrors(res.value.valid ? [] : res.value.errors ?? [])
      if (!res.value.valid || announceSuccess) report(res.value.valid ? 'ok' : 'error', res.value.valid ? t('configs.notice.valid') : t('configs.notice.validateFailed', { reason: (res.value.errors ?? []).map((error) => error.message).join('；') }))
      return res.value.valid
    } catch (error) {
      report('error', t('configs.notice.validateFailed', { reason: errorMessage(error) }))
      return false
    } finally {
      setValidating(false)
    }
  }

  const save = async (): Promise<boolean> => {
    if (busyRef.current || (props.readOnlyReason !== undefined && props.onSaveInstructions === undefined)) return false
    busyRef.current = true
    setSaving(true)
    try {
      if (props.readOnlyReason !== undefined) {
        const saved = await props.onSaveInstructions!()
        report(saved ? 'ok' : 'error', t(saved ? 'configs.notice.savedSnapshot' : 'configs.notice.saveIncomplete'))
        return saved
      }
      if (!await runValidate(configs, false)) return false
      setErrors([])
      const saved = await onSaveConfigs(configs)
      if (saved) report('ok', t('configs.notice.savedSnapshot'))
      else report('error', t('configs.notice.saveIncomplete'))
      return saved
    } catch (error) {
      report('error', t('configs.notice.saveFailed', { reason: errorMessage(error) }))
      return false
    } finally {
      setSaving(false)
      busyRef.current = false
    }
  }

  // 显示顺序（层序/order/声明序）一次计算：position map 供每张卡判断上移/下移，
  // 此前每张卡各自调 viewOrderedIds（O(n log n) × n）。
  // 世界书筛选视图：移动/排序只作用于可见子集（strategy=world-book），
  // 避免与不可见配置交换顺序。
  const viewStrategy = viewFilter === 'world-book' ? 'world-book' : undefined
  const viewIds = useMemo(
    () => viewOrderedIds(configs, effectiveLayer, allLayers, viewStrategy, ordered.filter(config => instructionFileIdOf(config) === undefined).map(config => config.id)),
    [configs, effectiveLayer, allLayers, viewStrategy, ordered],
  )

  /** 卡片稳定回调（memo 生效前提）：经 liveRef 读最新列表状态，回调引用跨渲染不变。 */
  const orderReadOnly = acrossModules ? moduleOrder.busy || saving || moduleOrder.snapshot === undefined : props.readOnlyReason !== undefined
  const liveRef = useRef({ configs, layer: effectiveLayer, metaLayers: allLayers, strategy: viewStrategy, dragId, dropTarget, viewIds, keyword, readOnly: orderReadOnly, acrossModules })
  liveRef.current = { configs, layer: effectiveLayer, metaLayers: allLayers, strategy: viewStrategy, dragId, dropTarget, viewIds, keyword, readOnly: orderReadOnly, acrossModules }
  const movableIds = (current: PromptConfigDraft[], id: string, ids: string[], across: boolean): string[] => {
    const source = current.find(config => config.id === id)
    if (!across || source === undefined) return ids
    const allowed = new Set(current.filter(config => sameConfigPosition(source, config)).map(config => config.id))
    return ids.filter(key => allowed.has(key))
  }
  const handleToggleExpanded = useCallback((id: string) => {
    setExpanded((current) => current === id ? undefined : id)
  }, [])
  const handleToggleEnabled = useCallback((id: string, enabled: boolean) => {
    const index = liveRef.current.configs.findIndex((item) => item.id === id)
    if (index >= 0) onPatchConfigs(liveRef.current.configs.map((item, at) => at === index ? { ...item, enabled } : item))
  }, [onPatchConfigs])
  const handlePatch = useCallback((id: string, patch: Partial<PromptConfigDraft>) => {
    if (patch.id !== undefined && patch.id !== id) {
      const prefix = `${props.draftScope}:${id}:`
      const previousFields = [...(props.fieldDrafts ?? [])] // Map迭代器会看到新键；改名必须使用旧条目的快照。
      for (const [key, field] of previousFields) if (key.startsWith(prefix)) {
        props.fieldDrafts?.set(`${props.draftScope}:${patch.id}:${key.slice(prefix.length)}`, field)
        props.fieldDrafts?.delete(key)
      }
    }
    const index = liveRef.current.configs.findIndex((item) => item.id === id)
    if (index >= 0) onPatchConfigs(liveRef.current.configs.map((item, at) => at === index ? { ...item, ...patch } : item))
  }, [onPatchConfigs, props.draftScope, props.fieldDrafts])
  const handleMove = useCallback((id: string, delta: -1 | 1) => {
    const { configs: current, layer: currentLayer, metaLayers, strategy, viewIds: ids, keyword: query, readOnly, acrossModules: across } = liveRef.current
    if (query || readOnly) return
    const index = current.findIndex((item) => item.id === id)
    if (index >= 0) onPatchConfigs(moveWithinLayer(current, index, delta, currentLayer, metaLayers, strategy, movableIds(current, id, ids, across)))
  }, [onPatchConfigs])
  const handleDuplicate = useCallback((id: string) => {
    const current = liveRef.current.configs
    const source = current.find((item) => item.id === id)
    if (source === undefined) return
    let nextId = `${source.id}-copy`
    let suffix = 2
    while (current.some((item) => item.id === nextId)) {
      nextId = `${source.id}-copy${suffix}`
      suffix += 1
    }
    const clone = JSON.parse(JSON.stringify(source)) as PromptConfigDraft
    clone.id = nextId
    onPatchConfigs([...current, clone])
    setExpanded(nextId)
    setCreatedId(nextId)
    onNotice('ok', t('configs.notice.copied'))
  }, [onNotice, onPatchConfigs, t])
  const handleDelete = useCallback((id: string) => {
    const current = liveRef.current.configs
    const ids = liveRef.current.viewIds
    const index = ids.indexOf(id)
    const nextId = ids[index + 1] ?? ids[index - 1]
    const prefix = `${props.draftScope}:${id}:`
    for (const key of props.fieldDrafts?.keys() ?? []) {
      if (key.startsWith(prefix)) props.fieldDrafts?.delete(key)
    }
    onPatchConfigs(current.filter((item) => item.id !== id))
    setExpanded((value) => value === id ? undefined : value)
    onNotice('ok', t('configs.notice.deleted'))
    requestAnimationFrame(() => {
      const target = nextId === undefined ? document.querySelector<HTMLElement>('[data-module-toolbar] button') : document.querySelector<HTMLElement>(`[data-config-id="${cssEscapeId(nextId)}"] button[aria-expanded]`)
      target?.focus()
    })
  }, [onNotice, onPatchConfigs, props.draftScope, props.fieldDrafts, t])
  const handleDragStart = useCallback((id: string, event: React.DragEvent<HTMLElement>) => {
    setDragId(id)
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', id)
  }, [])
  const handleDragOver = useCallback((id: string, event: React.DragEvent<HTMLElement>) => {
    const { dragId: currentDrag, configs: current, viewIds: ids, acrossModules: across, readOnly, keyword: query } = liveRef.current
    if (currentDrag === undefined || currentDrag === id) return
    if (readOnly || query || !movableIds(current, currentDrag, ids, across).includes(id)) { setDropTarget(undefined); return }
    event.preventDefault()
    const rect = event.currentTarget.getBoundingClientRect()
    setDropTarget({ id, before: event.clientY < rect.top + rect.height / 2 })
  }, [])
  const handleDrop = useCallback((id: string, event: React.DragEvent<HTMLElement>) => {
    event.preventDefault()
    const { configs: current, layer: currentLayer, metaLayers, strategy, dragId: source, dropTarget: target, viewIds: ids, keyword: query, readOnly, acrossModules: across } = liveRef.current
    if (query || readOnly) return
    if (source !== undefined && target !== undefined && source !== id) {
      onPatchConfigs(moveToView(current, source, target.id, target.before, currentLayer, metaLayers, strategy, movableIds(current, source, ids, across)))
    }
    setDragId(undefined)
    setDropTarget(undefined)
  }, [onPatchConfigs])
  const handleDragEnd = useCallback(() => {
    setDragId(undefined)
    setDropTarget(undefined)
  }, [])
  const handleMoveUp = useCallback((id: string) => handleMove(id, -1), [handleMove])
  const handleMoveDown = useCallback((id: string) => handleMove(id, 1), [handleMove])

  const renderCard = (config: PromptConfigDraft) => {
    const layerIds = movableIds(configs, config.id, ordered.filter((item) => promptConfigLayer(item) === promptConfigLayer(config) && instructionFileIdOf(item) === undefined).map((item) => item.id), acrossModules)
    const position = layerIds.indexOf(config.id)
    const sorting = keyword.length === 0 && !orderReadOnly && instructionFileIdOf(config) === undefined
    const showHandle = instructionFileIdOf(config) === undefined && (acrossModules ? moduleOrder.snapshot !== undefined : props.readOnlyReason === undefined)
    const entry = acrossModules ? moduleOrder.snapshot?.entries.find(item => configIdentityKey(item) === config.id) : undefined
    const moduleName = entry === undefined ? undefined : meta.presets?.find(module => module.id === entry.moduleId)?.name
    return (
      <PromptConfigCard
        key={config.id}
        t={t}
        meta={meta}
        config={config}
        fieldDrafts={acrossModules ? undefined : props.fieldDrafts}
        draftScope={props.draftScope}
        summaryOnly={acrossModules}
        sourceLabel={entry === undefined ? undefined : `${moduleName ?? entry.moduleId} · ${entry.moduleId} / ${entry.configId}`}
        disabled={orderReadOnly && instructionFileIdOf(config) === undefined}
        readOnlyReason={acrossModules || instructionFileIdOf(config) !== undefined ? undefined : props.readOnlyReason}
        expanded={!acrossModules && expanded === config.id}
        canMoveUp={sorting && position > 0}
        canMoveDown={sorting && position >= 0 && position < layerIds.length - 1}
        dragging={dragId === config.id}
        dragDisabled={!sorting}
        dropBefore={dropTarget?.id === config.id && dropTarget.before}
        dropAfter={dropTarget?.id === config.id && !dropTarget.before}
        onToggleExpanded={handleToggleExpanded}
        onToggleEnabled={handleToggleEnabled}
        onPatch={handlePatch}
        onMoveUp={handleMoveUp}
        onMoveDown={handleMoveDown}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
        onSaveInstructionFile={onSaveInstructionFile}
        onReloadInstructionFile={onReloadInstructionFile}
        onPatchInstructionPolicy={props.instructionPolicy?.error === undefined ? onPatchInstructionPolicy : undefined}
        onDragStart={showHandle ? handleDragStart : undefined}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragEnd={handleDragEnd}
        renderLayerSettings={acrossModules || props.hasLayerSettings?.(promptConfigLayer(config)) === false ? undefined : props.renderLayerSettings}
      />
    )
  }

  const clearFilters = (): void => {
    changeFilter('')
    changeViewFilter('all')
  }
  const hiddenCreated = !acrossModules && (props.createdHidden === true || (createdId !== undefined && configs.some((config) => config.id === createdId) && !ordered.some((config) => config.id === createdId)))
  /** 选中的是九层之一（world-book 是策略筛选，保持原有的「无匹配 + 清除筛选」提示）。 */
  const worldBookView = viewFilter === 'world-book'
  const layerView = viewFilter !== 'all' && !worldBookView

  return (
    <section className={styles.section} aria-labelledby="prompt-tool-configs-heading">
      <div className={styles.pageActions} data-workspace-sticky>
        <div className={styles.sectionHeading}>
          <div><h3 id="prompt-tool-configs-heading">{layer === undefined ? t('configs.heading.all') : t('configs.heading.layer')}</h3><p>{t('configs.meta', { total: scoped.length, enabled: scoped.filter((config) => config.enabled !== false).length })}</p></div>
          <div className={styles.sectionActions} data-module-toolbar="true">
            {acrossModules ? <button type="button" className={styles.pillButton} disabled={moduleOrder.busy || saving} onClick={() => { void moduleOrder.load() }}>{t(moduleOrder.error ? 'moduleOrder.discardRefresh' : 'moduleOrder.refresh')}</button> : <>{extraActions}
            {toolbarActions}
            <button type="button" className={styles.pillButton} disabled={validating || saving} onClick={() => void runValidate(configs)}>{validating && <span className={styles.spinner} aria-hidden="true" />}{validating ? t('configs.validating') : t('configs.validate')}</button>
            <button type="button" className={styles.primaryPill} disabled={saving || validating || (props.readOnlyReason !== undefined && props.onSaveInstructions === undefined)} onClick={save}>{saving && <span className={styles.spinner} aria-hidden="true" />}{saving ? t('configs.saving') : t('configs.save')}</button></>}
          </div>
        </div>
        <div className={styles.listFilterRow}>
          {props.onPrepareOrder !== undefined && <button type="button" className={styles.pillButton}
            disabled={saving || validating || moduleOrder.busy}
            onClick={() => { void changeRange(acrossModules ? 'current-module' : 'all-modules') }}>
            {t(acrossModules ? 'moduleOrder.current' : 'moduleOrder.all')}
          </button>}
          <input type="search" className={styles.listFilter} value={filter}
            aria-label={t('configs.filter.aria')} placeholder={t('configs.filter.placeholder')}
            spellCheck={false} onChange={(event) => changeFilter(event.target.value)} />
          {layer === undefined && <MenuSelect className={styles.listFilter} compact value={viewFilter} ariaLabel={t('configs.view.aria')}
            options={[
              { value: 'all', label: t('configs.view.all') },
              { value: 'world-book', label: t('configs.view.worldBook'), group: t('configs.view.strategy') },
              ...allLayers.map((item) => ({ value: item, label: t('configs.view.layer', { layer: translateLabel(t, LAYER_LABEL_KEYS, item) }), group: t('configs.view.insertion') })),
            ]} onChange={changeViewFilter} />}
          {!acrossModules && <span className={styles.batchControls}>
            <button type="button" className={styles.pillButton} data-batch="enable" disabled={batchConfigs.length === 0 || props.readOnlyReason !== undefined}
              onClick={() => batchSetEnabled(true)}>{t('configs.batch.enableVisible')}</button>
            {/* 停用是可逆的反向操作，但仍按危险操作呈现：复用删除按钮的描边染红形态（data-danger）。 */}
            <button type="button" className={styles.pillButton} data-danger data-batch="disable" disabled={batchConfigs.length === 0 || props.readOnlyReason !== undefined}
              onClick={() => batchSetEnabled(false)}>{t('configs.batch.disableVisible')}</button>
          </span>}
        </div>
        {acrossModules && <p className={styles.actionHint}>{t('moduleOrder.hint')}</p>}
        {acrossModules && moduleOrder.error && <p role="alert" className={styles.configErrorBox}>{moduleOrder.error}</p>}
        {acrossModules && moduleOrder.busy && <p role="status" className={styles.actionHint}>{t('app.loading')}</p>}
        {!acrossModules && props.readOnlyReason !== undefined && <p className={styles.actionHint}>{props.readOnlyReason} <button type="button" className={styles.pillButton} onClick={props.onChoosePreset}>{t('configs.chooseEditable')}</button></p>}
        {keyword.length > 0 && <p className={styles.actionHint}>{t('configs.searchSorting')}</p>}
        {hiddenCreated && <p className={styles.actionHint}>{t('configs.createdHidden')} <button type="button" className={styles.pillButton} onClick={() => {
          clearFilters()
          props.onShowCreated?.()
          if (createdId !== undefined) scrollToCreatedCard(`[data-config-id="${cssEscapeId(createdId)}"]`)
        }}>{t('configs.showAll')}</button></p>}
        {!acrossModules && errors.length > 0 && <div className={styles.configErrorBox}>
          {errors.map((error, index) => <div key={`${error.index}-${index}`} className={styles.configErrorLine}>[{error.index}] {error.id || t('configs.error.missingId')}：{error.message}</div>)}
        </div>}
      </div>

      {!acrossModules && props.instructionPolicy?.error !== undefined && <p role="alert" className={styles.configErrorBox}>{props.instructionPolicy.error}</p>}
      {!acrossModules && beforeCards}

      {ordered.length === 0 ? acrossModules ? <p className={styles.readOnly}>{t('moduleOrder.empty')}</p> : (keyword.length > 0 || worldBookView) ? (
        <p className={styles.readOnly}>{t('configs.noMatch', { keyword: filter.trim() || translateLabel(t, LAYER_LABEL_KEYS, viewFilter) })} <button type="button" className={styles.pillButton} onClick={clearFilters}>{t('configs.clearFilters')}</button></p>
      ) : layerView ? (
        // 没有真实实例就显示空状态与新增入口，不根据引擎设置自动补卡。
        <div className={styles.emptyState}><span className={styles.emptyGlyph} aria-hidden="true">⌁</span><div>
          <h3>{t('configs.empty.layer.title', { layer: translateLabel(t, LAYER_LABEL_KEYS, viewFilter) })}</h3>
          <p>{t('configs.empty.layer.desc')}</p>
          {emptyHint !== undefined && <p className={styles.readOnly}>{emptyHint}</p>}
          {props.readOnlyReason === undefined && props.onCreate !== undefined && <button type="button" className={styles.pillButton} onClick={props.onCreate}>{t('configs.createFirst')}</button>}
          {props.readOnlyReason !== undefined && props.onChoosePreset !== undefined && <button type="button" className={styles.pillButton} onClick={props.onChoosePreset}>{t('configs.chooseEditable')}</button>}
        </div></div>
      ) : (
        <div className={styles.emptyState}><span className={styles.emptyGlyph} aria-hidden="true">⌁</span><div>
          <h3>{scope === 'subagent' ? t('configs.empty.subagent.title') : t('configs.empty.all.title')}</h3>
          <p>{scope === 'subagent' ? t('configs.empty.subagent.desc') : t('configs.empty.all.desc')}</p>
          {emptyHint !== undefined && <p className={styles.readOnly}>{emptyHint}</p>}
          {props.readOnlyReason === undefined && props.onCreate !== undefined && <button type="button" className={styles.pillButton} onClick={props.onCreate}>{t('configs.createFirst')}</button>}
          {props.readOnlyReason !== undefined && props.onChoosePreset !== undefined && <button type="button" className={styles.pillButton} onClick={props.onChoosePreset}>{t('configs.chooseEditable')}</button>}
        </div></div>
      ) : <div className={styles.configList}>
        {ordered.map((config) => renderCard(config))}
      </div>}

    </section>
  )
}
